// THE LEAD OUTCOME LIST: defined ONCE, here (Speed to Lead Stage 4.5).
//
// ⚠️⚠️ There is no second outcome list or label map anywhere: not in SMS, calls, the Kyck UI, the portal, the prompts or
// reporting. Every one of them imports this table. A guard test scans the source for the old codes and for any other
// table of these labels. The only place the LEGACY codes may appear is `src/lib/leads/legacyOutcomes.ts` (the one-off
// migration mapping).

export type OutcomeStage = 'not_reached' | 'reached' | 'committed' | 'sale';

export type LeadOutcome =
  | 'no_response'
  | 'wrong_number'
  | 'existing_member'
  | 'not_interested'
  | 'still_deciding'
  | 'callback_booked'
  | 'tour_booked'
  | 'joined'
  /** The starting state: nobody (and nothing) has classified this lead yet. */
  | 'unresolved';

/** code -> label -> stage. `unresolved` has no stage: it is the start, below the whole funnel. */
export const LEAD_OUTCOME_TABLE: Record<LeadOutcome, { label: string; stage: OutcomeStage | null }> = {
  no_response: { label: 'No response', stage: 'not_reached' },
  wrong_number: { label: 'Wrong number / spam', stage: 'not_reached' },
  existing_member: { label: 'Existing member', stage: 'not_reached' },
  not_interested: { label: 'Not interested', stage: 'reached' },
  still_deciding: { label: 'Still deciding', stage: 'reached' },
  callback_booked: { label: 'Call back booked', stage: 'committed' },
  tour_booked: { label: 'Tour booked', stage: 'committed' },
  joined: { label: 'Joined', stage: 'sale' },
  unresolved: { label: 'Not classified yet', stage: null },
};

/** Every code, in funnel order (the start last, as a picker lists it). */
export const LEAD_OUTCOMES: LeadOutcome[] = [
  'no_response',
  'wrong_number',
  'existing_member',
  'not_interested',
  'still_deciding',
  'callback_booked',
  'tour_booked',
  'joined',
  'unresolved',
];

/** What a model may suggest and a picker offers as a real classification: everything except the start state. */
export const CONFIRMABLE_LEAD_OUTCOMES: LeadOutcome[] = LEAD_OUTCOMES.filter((o) => o !== 'unresolved');

export const LEAD_OUTCOME_LABELS: Record<LeadOutcome, string> = Object.fromEntries(
  LEAD_OUTCOMES.map((o) => [o, LEAD_OUTCOME_TABLE[o].label]),
) as Record<LeadOutcome, string>;

export const OUTCOME_STAGE_ORDER: OutcomeStage[] = ['not_reached', 'reached', 'committed', 'sale'];

export const OUTCOME_STAGE_LABELS: Record<OutcomeStage, string> = {
  not_reached: 'Not reached',
  reached: 'Reached, no commitment',
  committed: 'Committed',
  sale: 'Sale',
};

/**
 * The funnel rank used by the forward-only rule: unresolved (0) < not_reached < reached < committed < sale.
 * Within a stage a suggestion never moves the outcome sideways (equal rank is not "higher").
 */
export function outcomeRank(outcome: LeadOutcome): number {
  const stage = LEAD_OUTCOME_TABLE[outcome].stage;
  return stage === null ? 0 : OUTCOME_STAGE_ORDER.indexOf(stage) + 1;
}

export const isLeadOutcome = (v: unknown): v is LeadOutcome => typeof v === 'string' && (LEAD_OUTCOMES as readonly string[]).includes(v);
export const isConfirmableLeadOutcome = (v: unknown): v is LeadOutcome => typeof v === 'string' && (CONFIRMABLE_LEAD_OUTCOMES as readonly string[]).includes(v);

/** The conversion figure counts a lead that is Committed or a Sale (call back booked, tour booked, joined). */
export const CONVERSION_OUTCOMES: LeadOutcome[] = LEAD_OUTCOMES.filter((o) => {
  const stage = LEAD_OUTCOME_TABLE[o].stage;
  return stage === 'committed' || stage === 'sale';
});
export const isConversionOutcome = (o: LeadOutcome): boolean => CONVERSION_OUTCOMES.includes(o);
